/**
 * API Service Layer
 * Centralized API client with TypeScript support, CSRF handling, and error management
 */

import axios, {
  AxiosResponse,
  AxiosError,
  AxiosRequestConfig,
  AxiosHeaders,
} from 'axios';
import {
  ApiError,
  AppConfig,
  AnalyticsChartResponse,
  BagWasteEntryPayload,
  BagWasteResponse,
  CalendarizedCO2eResponse,
  CalendarizedConsumptionResponse,
  CalendarizedEmissionsByScopeResponse,
  ChartFilters,
  ClientEntry,
  ComplianceDocument,
  ComplianceDocumentDeleteResponse,
  ComplianceDocumentDetail,
  ComplianceDocumentDownloadResponse,
  ComplianceDocumentFinalizeRequest,
  ComplianceDocumentsListParams,
  ComplianceDocumentsListResponse,
  ComplianceDocumentUpdateRequest,
  ComplianceOcrRetryResponse,
  ComplianceReminderFeedResponse,
  ComplianceUploadInitRequest,
  ComplianceUploadInitResponse,
  ConsumptionEntry,
  CoversVolume,
  CoversVolumeCreate,
  CoversVolumeStats,
  CoversVolumeUpdate,
  CreateGoalRequest,
  DashboardData,
  Goal,
  Invoice,
  ManualConsumptionEntryPayload,
  OcrTaskStartResponse,
  PaginatedResponse,
  PaginatedTransactions,
  PaginatedUnifiedTransactions,
  ProgressScoreSeriesResponse,
  ProgressScoreSummaryResponse,
  RestaurantProfile,
  SalesDocumentConfirmation,
  Supplier,
  SystemHealth,
  TotalConsumptionChartResponse,
  TransactionSummary,
  UnifiedTransactionFilters,
  UpdateGoalRequest,
  User,
  AnalyticsFilters,
  AnalyticsSummary,
  SmartSortingUploadInitRequest,
  SmartSortingUploadInitResponse,
  SmartSortingFinalizeRequest,
  SmartSortingFinalizeResponse,
  SmartSortingSummaryResponse,
  SmartSortingFilesListParams,
  SmartSortingFilesListResponse,
  InvoiceFileDownloadResponse,
  ReportJobDownloadResponse,
  SmartSortingFileDownloadResponse,
  UserFilesListParams,
  UserFilesListResponse,
  DomainShareScope,
  QrShareCreateRequest,
  QrShareDetail,
  QrShareListParams,
  QrShareListResponse,
  QrShareScopePreviewResponse,
  QrShareUpdateRequest,
} from '../types/api';
import {
  parseTaskStatusListResponse,
  parseTaskStatusResponse,
  ParsedTaskStatusListResponse,
  ParsedTaskStatusResponse,
} from '../lib/schemas/task.schema';

import {
  LoginForm,
  RegisterForm,
  DevLoginForm,
  RestaurantProfileForm,
  InvoiceEditForm,
  ClientesForm,
  SupplierCreateForm,
  SupplierUpdateForm,
} from '../types/forms';
import {
  MARKETING_REPORT_DEFAULT_TYPE,
  MARKETING_REPORT_DEFAULT_VERSION,
  ReportGenerationJobResponse,
  ReportGenerationJobStatusResponse,
  ReportGenerationRequestPayload,
} from '../features/reports/types';
import { createApiLogger } from '../utils/logger';
import { register401Interceptor } from '../utils/auth/register401Interceptor';
import { resetClientAuthState } from '../utils/auth/resetClientAuthState';
import { isPublicSharePath } from '../utils/publicRoutes';

// ============================================================================
// Configuration & Constants
// ============================================================================

// In production, point directly to backend domain for reliable cookie sharing
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const REQUEST_TIMEOUT = 30000; // 30 seconds
const CSRF_HEADER_NAME = 'X-CSRF-Token';

// CSRF token cache
let cachedCsrfToken: string | null = null;
let csrfTokenPromise: Promise<string> | null = null;

// ============================================================================
// Axios Instance Configuration
// ============================================================================

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

register401Interceptor(api);

// ============================================================================
// Request Interceptor - CSRF Token Handling
// ============================================================================

api.interceptors.request.use(
  async (config) => {
    const method = config.method?.toLowerCase() ?? '';
    const needsCsrf = ['post', 'put', 'patch', 'delete'].includes(method);

    if (needsCsrf) {
      let token = cachedCsrfToken;
      if (!token) {
        token = await ensureCsrfToken();
      }

      if (token) {
        if (!config.headers) {
          config.headers = new AxiosHeaders();
        }

        if (config.headers instanceof AxiosHeaders) {
          config.headers.set(CSRF_HEADER_NAME, token);
        } else {
          (config.headers as Record<string, string>)[CSRF_HEADER_NAME] = token;
        }
      }
    }

    if (config.data instanceof FormData && config.headers) {
      if (config.headers instanceof AxiosHeaders) {
        config.headers.delete('Content-Type');
      } else {
        delete config.headers['Content-Type'];
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ============================================================================
// Error Detail Extraction
// ============================================================================

/**
 * Robustly extract an error message from an Axios response `data` payload.
 *
 * Returns the extracted message, or `''` when the detail cannot be determined
 * so callers can provide their own contextual fallback.
 *
 * Handles multiple shapes:
 *   - `{ detail: "string" }`               — FastAPI HTTPException
 *   - `{ detail: [{msg: "string"}, ...] }` — Pydantic 422 validation
 *   - `{ message: "string" }`              — generic API errors
 *   - raw JSON string                      — proxy didn't parse body
 *   - plain string                         — raw text body
 */
export function extractErrorDetail(data: unknown): string {
  if (data === null || data === undefined) return '';

  if (typeof data === 'string') {
    try {
      const parsed: unknown = JSON.parse(data);
      return extractErrorDetail(parsed);
    } catch {
      return data;
    }
  }

  if (typeof data !== 'object') return '';

  const obj = data as Record<string, unknown>;

  if (typeof obj.detail === 'string' && obj.detail) return obj.detail;

  if (Array.isArray(obj.detail)) {
    const messages = obj.detail
      .map((entry: unknown) => {
        if (typeof entry === 'string') return entry;
        if (typeof entry === 'object' && entry !== null) {
          const e = entry as Record<string, unknown>;
          if (typeof e.msg === 'string') return e.msg;
          if (typeof e.message === 'string') return e.message;
        }
        return '';
      })
      .filter(Boolean);
    if (messages.length > 0) return messages.join('. ');
  }

  if (typeof obj.detail === 'object' && obj.detail !== null) {
    const nestedDetail = extractErrorDetail(obj.detail);
    if (nestedDetail) return nestedDetail;
  }

  if (typeof obj.message === 'string' && obj.message) return obj.message;
  if (typeof obj.error === 'string' && obj.error) return obj.error;

  return '';
}

// ============================================================================
// Response Interceptor - Error Handling
// ============================================================================

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError<ApiError>) => {
    // Handle network errors
    if (!error.response) {
      // More specific error handling for different scenarios
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        const timeoutError = new Error(
          'La solicitud tardó demasiado. Intenta de nuevo.',
        );
        timeoutError.name = 'TimeoutError';
        return Promise.reject(timeoutError);
      }

      if (
        error.message.includes('Network Error') ||
        error.code === 'ERR_NETWORK'
      ) {
        const networkError = new Error(
          'Error de conexión. Verifica tu internet.',
        );
        networkError.name = 'NetworkError';
        return Promise.reject(networkError);
      }

      // For upload-related errors that complete but fail parsing
      if (error.config?.url?.includes('/scan-invoice/')) {
        const uploadError = new Error(
          'Error al procesar la respuesta del servidor. La factura puede haberse procesado correctamente.',
        );
        uploadError.name = 'UploadResponseError';
        return Promise.reject(uploadError);
      }

      // Generic fallback
      const unknownError = new Error(
        `Error desconocido: ${error.message || 'Sin detalles'}`,
      );
      unknownError.name = 'UnknownError';
      return Promise.reject(unknownError);
    }

    // Handle authentication errors
    if (error.response.status === 401) {
      resetClientAuthState();

      if (typeof window !== 'undefined') {
        const path = window.location.pathname;
        const isAuthRoute = path.startsWith('/auth/');
        if (
          !isAuthRoute &&
          !path.includes('/auth/login') &&
          !isPublicSharePath(path)
        ) {
          window.location.href = '/auth/login';
        }
      }
    }

    // Handle CSRF token errors
    const csrfDetail = extractErrorDetail(error.response.data);
    if (error.response.status === 403 && csrfDetail.includes('CSRF')) {
      // Try to refresh CSRF token and retry the request
      return ensureCsrfToken(true)
        .then(() => api.request(error.config!))
        .catch(() => Promise.reject(error));
    }

    // Transform error response — extract detail robustly.
    // Widen to `unknown` because the actual runtime payload may not match the
    // generic `ApiError` that AxiosError<ApiError> declares.
    const rawData: unknown = error.response.data;
    const detail = extractErrorDetail(rawData);

    if (!detail) {
      console.error(
        '[API Interceptor] Could not extract error detail from response:',
        {
          status: error.response.status,
          dataType: typeof rawData,
          data: rawData,
        },
      );
    }

    const raw =
      typeof rawData === 'object' && rawData !== null
        ? (rawData as Record<string, unknown>)
        : null;

    const apiError: ApiError = {
      detail: detail || `Error ${error.response.status}`,
      ...(raw &&
        typeof raw.code === 'string' && {
          code: raw.code,
        }),
      ...(raw &&
        typeof raw.field === 'string' && {
          field: raw.field,
        }),
      timestamp:
        (raw && typeof raw.timestamp === 'string'
          ? raw.timestamp
          : undefined) || new Date().toISOString(),
    };

    // Enrich the original AxiosError instead of replacing it.
    // Pattern A consumers use `error.response?.data?.detail`.
    // Pattern B consumers use `error.detail` / `error.status`.
    // By keeping the AxiosError and attaching apiError fields, both work.
    error.response.data = apiError;
    Object.assign(error, {
      detail: apiError.detail,
      status: error.response.status,
    } satisfies Record<string, unknown>);

    return Promise.reject(error);
  },
);

// ============================================================================
// API Service Class
// ============================================================================

class ApiService {
  // Request deduplication cache
  private pendingRequests = new Map<string, Promise<unknown>>();
  private readonly logger = createApiLogger('ApiService');

  // Helper method to deduplicate concurrent requests
  private async deduplicateRequest<T>(
    key: string,
    requestFn: () => Promise<T>,
  ): Promise<T> {
    // If request is already pending, return the existing promise
    if (this.pendingRequests.has(key)) {
      const existingRequest = this.pendingRequests.get(key);
      if (existingRequest) {
        return existingRequest as Promise<T>;
      }
    }

    // Create new request promise
    const requestPromise = requestFn().finally(() => {
      // Clean up after request completes (success or failure)
      this.pendingRequests.delete(key);
    });

    // Store the promise
    this.pendingRequests.set(key, requestPromise);

    return requestPromise;
  }

  // --------------------------------------------------------------------------
  // System & Health Endpoints
  // --------------------------------------------------------------------------

  /**
   * Get CSRF token - ensures CSRF cookie is set
   */
  async csrf(): Promise<string> {
    return ensureCsrfToken(true);
  }

  /**
   * Get system health status
   */
  async health(): Promise<SystemHealth> {
    const response = await api.get<SystemHealth>('/health');
    return response.data;
  }

  /**
   * Get Gemini AI service health
   */
  async geminiHealth(): Promise<{ status: string; message?: string }> {
    const response = await api.get('/health/gemini');
    return response.data;
  }

  /**
   * Get application configuration
   */
  async config(): Promise<AppConfig> {
    const response = await api.get<AppConfig>('/config');
    return response.data;
  }

  // --------------------------------------------------------------------------
  // Authentication Endpoints
  // --------------------------------------------------------------------------

  /**
   * Get current user information
   */
  async me(): Promise<User> {
    const response = await api.get<{ user: User }>('/me');
    return response.data.user;
  }

  /**
   * Login with email and password
   */
  async login(data: LoginForm): Promise<AxiosResponse<{ user: User }>> {
    return api.post<{ user: User }>('/login', data);
  }

  /**
   * Register new user account
   */
  async register(data: RegisterForm): Promise<AxiosResponse> {
    return api.post('/register', data);
  }

  /**
   * Development login (only available in dev mode)
   */
  async devLogin(data: DevLoginForm): Promise<AxiosResponse<{ user: User }>> {
    return api.post<{ user: User }>('/dev/login', data);
  }

  /**
   * Quick signin for development (creates user if not exists)
   */
  async devQuickSignin(data: {
    email?: string;
    name?: string;
  }): Promise<AxiosResponse<{ user: User }>> {
    return api.post<{ user: User }>('/dev/quick-signin', data);
  }

  /**
   * Logout current user
   */
  async logout(): Promise<AxiosResponse> {
    return api.post('/logout');
  }

  /**
   * Request account linking between providers
   */
  async requestAccountLinking(data: {
    provider: string;
    provider_subject: string;
    provider_email: string;
  }): Promise<
    AxiosResponse<{
      message: string;
      expires_in_minutes: number;
      token_hint: string;
      delivery: {
        channel: string;
        instructions: string;
      };
    }>
  > {
    return api.post('/auth/link/request', data);
  }

  /**
   * Confirm account linking with token
   */
  async confirmAccountLinking(data: { token: string }): Promise<
    AxiosResponse<{
      message: string;
      user: User;
    }>
  > {
    return api.post('/auth/link/confirm', data);
  }

  /**
   * Cancel account linking request
   */
  async cancelAccountLinking(data: {
    token: string;
  }): Promise<AxiosResponse<{ message: string }>> {
    return api.post('/auth/link/cancel', data);
  }

  /**
   * Request password reset
   */
  async forgotPassword(email: string): Promise<
    AxiosResponse<{
      detail?: string;
      token_hint?: string;
      delivery?: {
        channel: string;
        instructions: string;
      };
    }>
  > {
    return api.post('/password/forgot', { email });
  }

  /**
   * Reset password with token
   */
  async resetPassword(data: {
    token: string;
    new_password: string;
  }): Promise<AxiosResponse> {
    return api.post('/password/reset', data);
  }

  /**
   * Resend email verification
   */
  async resendVerification(email: string): Promise<
    AxiosResponse<{
      detail?: string;
      token_hint?: string;
      delivery?: {
        channel: string;
        instructions: string;
      };
    }>
  > {
    return api.post('/verify/resend', { email });
  }

  // --------------------------------------------------------------------------
  // Profile Management Endpoints
  // --------------------------------------------------------------------------

  /**
   * Get restaurant profile
   */
  async getRestaurantProfile(): Promise<RestaurantProfile> {
    return this.deduplicateRequest('getRestaurantProfile', async () => {
      try {
        const response = await api.get<RestaurantProfile>(
          '/profile/restaurant',
        );
        return response.data;
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        this.logger.error('getRestaurantProfile failed', err);
        throw error;
      }
    });
  }

  /**
   * Create restaurant profile
   */
  async createRestaurantProfile(data: RestaurantProfileForm): Promise<{
    message: string;
    profile: RestaurantProfile;
  }> {
    const response = await api.post('/profile/restaurant', data);
    return response.data;
  }

  // --------------------------------------------------------------------------
  // Supplier Management
  // --------------------------------------------------------------------------

  /**
   * Get all suppliers for the organization
   */
  async getSuppliers(): Promise<Supplier[]> {
    return this.deduplicateRequest('getSuppliers', async () => {
      try {
        const response = await api.get<Supplier[]>('/suppliers');
        return response.data;
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        this.logger.error('getSuppliers failed', err);
        throw error;
      }
    });
  }

  /**
   * Get a specific supplier by ID
   */
  async getSupplier(id: number): Promise<Supplier> {
    this.logger.debug('getSupplier request', undefined, { id });
    try {
      const response = await api.get<Supplier>(`/suppliers/${id}`);
      this.logger.info('getSupplier success');
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('getSupplier failed', err, undefined, { id });
      throw error;
    }
  }

  /**
   * Create a new supplier
   */
  async createSupplier(data: SupplierCreateForm): Promise<Supplier> {
    this.logger.debug('createSupplier request', undefined, { data });
    try {
      const response = await api.post<Supplier>('/suppliers', data);
      this.logger.info('createSupplier success', undefined, {
        id: response.data.id,
      });
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('createSupplier failed', err, undefined, { data });
      throw error;
    }
  }

  /**
   * Update an existing supplier
   */
  async updateSupplier(
    id: number,
    data: SupplierUpdateForm,
  ): Promise<Supplier> {
    this.logger.debug('updateSupplier request', undefined, { id, data });
    try {
      const response = await api.put<Supplier>(`/suppliers/${id}`, data);
      this.logger.info('updateSupplier success', undefined, { id });
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('updateSupplier failed', err, undefined, { id });
      throw error;
    }
  }

  /**
   * Delete a supplier
   */
  async deleteSupplier(id: number): Promise<void> {
    this.logger.debug('deleteSupplier request', undefined, { id });
    try {
      await api.delete(`/suppliers/${id}`);
      this.logger.info('deleteSupplier success', undefined, { id });
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('deleteSupplier failed', err, undefined, { id });
      throw error;
    }
  }

  /**
   * Update restaurant profile
   */
  async updateRestaurantProfile(
    data: Partial<RestaurantProfileForm>,
  ): Promise<RestaurantProfile> {
    const response = await api.put<RestaurantProfile>(
      '/profile/restaurant',
      data,
    );
    return response.data;
  }

  // --------------------------------------------------------------------------
  // Dashboard Endpoints
  // --------------------------------------------------------------------------

  /**
   * Get dashboard summary data
   */
  async getDashboardSummary(): Promise<DashboardData> {
    const response = await api.get<DashboardData>('/dashboard/summary');
    return response.data;
  }

  // --------------------------------------------------------------------------
  // Compliance Endpoints
  // --------------------------------------------------------------------------

  /**
   * Initialize compliance document upload (presigned PUT).
   */
  async initComplianceUpload(
    data: ComplianceUploadInitRequest,
  ): Promise<ComplianceUploadInitResponse> {
    this.logger.debug('initComplianceUpload request', undefined, {
      size_bytes: data.size_bytes,
      mime_type: data.mime_type,
    });
    try {
      const response = await api.post<ComplianceUploadInitResponse>(
        '/compliance/documents/init',
        data,
      );
      this.logger.info('initComplianceUpload success', undefined, {
        document_id: response.data.document_id,
      });
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('initComplianceUpload failed', err);
      throw error;
    }
  }

  /**
   * Start OCR extraction for compliance documents.
   */
  async startComplianceOcr(data: FormData): Promise<OcrTaskStartResponse> {
    this.logger.debug('startComplianceOcr request');
    try {
      const response = await api.post<OcrTaskStartResponse>(
        '/compliance/documents/ocr',
        data,
      );
      this.logger.info('startComplianceOcr success');
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('startComplianceOcr failed', err);
      throw error;
    }
  }

  /**
   * Finalize compliance document after upload and validation.
   */
  async finalizeComplianceDocument(
    data: ComplianceDocumentFinalizeRequest,
    idempotencyKey?: string,
  ): Promise<ComplianceDocument> {
    const resolvedIdempotencyKey =
      idempotencyKey ??
      (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

    this.logger.debug('finalizeComplianceDocument request', undefined, {
      document_id: data.document_id,
      type: data.type,
    });

    try {
      const response = await api.post<ComplianceDocument>(
        '/compliance/documents',
        data,
        {
          headers: {
            'Idempotency-Key': resolvedIdempotencyKey,
          },
        },
      );
      this.logger.info('finalizeComplianceDocument success', undefined, {
        document_id: response.data.id,
      });
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('finalizeComplianceDocument failed', err, undefined, {
        document_id: data.document_id,
      });
      throw error;
    }
  }

  /**
   * List compliance documents with filters.
   */
  async getComplianceDocuments(
    params: ComplianceDocumentsListParams = {},
  ): Promise<ComplianceDocumentsListResponse> {
    const queryParams = new URLSearchParams();

    if (params.page !== undefined) {
      queryParams.append('page', params.page.toString());
    }
    if (params.per_page !== undefined) {
      queryParams.append('per_page', params.per_page.toString());
    }
    if (params.type) {
      queryParams.append('type', params.type);
    }
    if (params.status && params.status.length > 0) {
      params.status.forEach((status) => {
        queryParams.append('status', status);
      });
    }
    if (params.expiry_from) {
      queryParams.append('expiry_from', params.expiry_from);
    }
    if (params.expiry_to) {
      queryParams.append('expiry_to', params.expiry_to);
    }
    if (params.search) {
      queryParams.append('search', params.search);
    }
    if (params.tags && params.tags.length > 0) {
      params.tags.forEach((tag) => {
        queryParams.append('tags', tag);
      });
    }
    if (params.sort_by) {
      queryParams.append('sort_by', params.sort_by);
    }
    if (params.sort_dir) {
      queryParams.append('sort_dir', params.sort_dir);
    }

    const queryString = queryParams.toString();
    const path = queryString
      ? `/compliance/documents?${queryString}`
      : '/compliance/documents';

    this.logger.debug('getComplianceDocuments request', undefined, {
      params,
    });

    try {
      const response = await api.get<ComplianceDocumentsListResponse>(path);
      this.logger.info('getComplianceDocuments success', undefined, {
        total: response.data.total,
      });
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('getComplianceDocuments failed', err, undefined, {
        params,
      });
      throw error;
    }
  }

  /**
   * Get compliance document detail.
   */
  async getComplianceDocument(
    documentId: string,
  ): Promise<ComplianceDocumentDetail> {
    this.logger.debug('getComplianceDocument request', undefined, {
      documentId,
    });
    try {
      const response = await api.get<ComplianceDocumentDetail>(
        `/compliance/documents/${documentId}`,
      );
      this.logger.info('getComplianceDocument success', undefined, {
        documentId,
      });
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('getComplianceDocument failed', err, undefined, {
        documentId,
      });
      throw error;
    }
  }

  /**
   * Generate a presigned download URL and record a download event.
   */
  async downloadComplianceDocument(
    documentId: string,
  ): Promise<ComplianceDocumentDownloadResponse> {
    this.logger.debug('downloadComplianceDocument request', undefined, {
      documentId,
    });
    try {
      const response = await api.post<ComplianceDocumentDownloadResponse>(
        `/compliance/documents/${documentId}/download`,
      );
      this.logger.info('downloadComplianceDocument success', undefined, {
        documentId,
      });
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('downloadComplianceDocument failed', err, undefined, {
        documentId,
      });
      throw error;
    }
  }

  /**
   * Update compliance document metadata or status.
   */
  async updateComplianceDocument(
    documentId: string,
    data: ComplianceDocumentUpdateRequest,
  ): Promise<ComplianceDocument> {
    this.logger.debug('updateComplianceDocument request', undefined, {
      documentId,
    });
    try {
      const response = await api.put<ComplianceDocument>(
        `/compliance/documents/${documentId}`,
        data,
      );
      this.logger.info('updateComplianceDocument success', undefined, {
        documentId,
      });
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('updateComplianceDocument failed', err, undefined, {
        documentId,
      });
      throw error;
    }
  }

  /**
   * Delete compliance document (soft delete by default).
   */
  async deleteComplianceDocument(
    documentId: string,
    hard?: boolean,
  ): Promise<ComplianceDocumentDeleteResponse> {
    this.logger.debug('deleteComplianceDocument request', undefined, {
      documentId,
      hard,
    });
    try {
      const response = await api.delete<ComplianceDocumentDeleteResponse>(
        `/compliance/documents/${documentId}`,
        hard === true ? { params: { hard: true } } : undefined,
      );
      this.logger.info('deleteComplianceDocument success', undefined, {
        documentId,
      });
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('deleteComplianceDocument failed', err, undefined, {
        documentId,
        hard,
      });
      throw error;
    }
  }

  /**
   * Retry compliance OCR extraction.
   */
  async retryComplianceOcr(
    documentId: string,
  ): Promise<ComplianceOcrRetryResponse> {
    this.logger.debug('retryComplianceOcr request', undefined, { documentId });
    try {
      const response = await api.post<ComplianceOcrRetryResponse>(
        `/compliance/documents/${documentId}/ocr/retry`,
      );
      this.logger.info('retryComplianceOcr success', undefined, {
        documentId,
      });
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('retryComplianceOcr failed', err, undefined, {
        documentId,
      });
      throw error;
    }
  }

  /**
   * Get compliance reminders feed for dashboard.
   */
  async getComplianceReminders(): Promise<ComplianceReminderFeedResponse> {
    this.logger.debug('getComplianceReminders request');
    try {
      const response = await api.get<ComplianceReminderFeedResponse>(
        '/compliance/reminders/compliance',
      );
      this.logger.info('getComplianceReminders success', undefined, {
        count: response.data.items.length,
      });
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('getComplianceReminders failed', err);
      throw error;
    }
  }

  // --------------------------------------------------------------------------
  // Invoice Management Endpoints
  // --------------------------------------------------------------------------

  /**
   * Scan invoice file with AI processing
   */
  async scanInvoice(formData: FormData): Promise<OcrTaskStartResponse> {
    const response = await api.post<OcrTaskStartResponse>(
      '/scan-invoice/',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // Extended timeout for file processing
      },
    );
    return response.data;
  }

  /**
   * Check invoice OCR processing status
   */
  async scanSalesDocument(formData: FormData): Promise<OcrTaskStartResponse> {
    const response = await api.post<OcrTaskStartResponse>(
      '/scan-sales-document/',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // Extended timeout for file processing
      },
    );
    return response.data;
  }

  /**
   * Check sales document OCR processing status
   *
   * @deprecated Use getTaskStatus for the unified async tasks API.
   */
  async getOcrTaskStatus(taskId: string): Promise<ParsedTaskStatusResponse> {
    return this.getTaskStatus(taskId);
  }

  // --------------------------------------------------------------------------
  // Async Task Endpoints (Unified)
  // --------------------------------------------------------------------------

  /**
   * Fetch unified task status for a given task id.
   * Validates response with TaskStatusResponseSchema to avoid contract drift.
   */
  async getTaskStatus(taskId: string): Promise<ParsedTaskStatusResponse> {
    const response = await api.get(
      `/tasks/${encodeURIComponent(taskId)}/status`,
    );
    return parseTaskStatusResponse(response.data);
  }

  /**
   * Fetch all active async tasks for the authenticated user.
   */
  async getActiveTasks(): Promise<ParsedTaskStatusListResponse> {
    const response = await api.get('/tasks/active');
    return parseTaskStatusListResponse(response.data);
  }

  // --------------------------------------------------------------------------
  // Marketing Report Endpoints
  // --------------------------------------------------------------------------

  /**
   * Initiate the asynchronous marketing report generation workflow.
   */
  async postMarketingReport({
    restaurantId,
    payload,
    idempotencyKey,
    reportType,
  }: {
    restaurantId: number | string;
    payload: ReportGenerationRequestPayload;
    idempotencyKey?: string;
    reportType?: string;
  }): Promise<ReportGenerationJobResponse> {
    const resolvedIdempotencyKey =
      idempotencyKey ??
      (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

    const resolvedPayload: ReportGenerationRequestPayload = {
      ...payload,
      ...(reportType ? { reportType } : {}),
    };

    if (!resolvedPayload.reportType) {
      resolvedPayload.reportType = MARKETING_REPORT_DEFAULT_TYPE;
    }

    if (resolvedPayload.version === undefined) {
      resolvedPayload.version = MARKETING_REPORT_DEFAULT_VERSION;
    }

    const path = `/reports/restaurant/${encodeURIComponent(String(restaurantId))}`;

    this.logger.debug('postMarketingReport request', undefined, {
      restaurantId,
      reportType: resolvedPayload.reportType,
      hasTemplate: Boolean(resolvedPayload.templateId),
    });

    try {
      const response = await api.post<ReportGenerationJobResponse>(
        path,
        resolvedPayload,
        {
          headers: {
            'Idempotency-Key': resolvedIdempotencyKey,
          },
        },
      );

      this.logger.info('postMarketingReport success', undefined, {
        jobId: response.data.jobId,
        status: response.data.status,
      });

      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('postMarketingReport failed', err, undefined, {
        restaurantId,
      });
      throw error;
    }
  }

  /**
   * Retrieve the current status of a marketing report job.
   */
  async getMarketingReportJob(
    jobId: string,
  ): Promise<ReportGenerationJobStatusResponse> {
    const path = `/reports/jobs/${encodeURIComponent(jobId)}`;

    this.logger.debug('getMarketingReportJob request', undefined, { jobId });

    try {
      const response = await api.get<ReportGenerationJobStatusResponse>(path);

      this.logger.info('getMarketingReportJob success', undefined, {
        jobId,
        status: response.data.status,
      });

      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('getMarketingReportJob failed', err, undefined, {
        jobId,
      });
      throw error;
    }
  }

  async downloadReportJob(jobId: string): Promise<ReportJobDownloadResponse> {
    const response = await api.get<ReportJobDownloadResponse>(
      `/reports/jobs/${encodeURIComponent(jobId)}/download`,
    );
    return response.data;
  }

  /**
   * Confirm and save sales document data after review
   */
  async confirmSalesDocument(
    confirmationData: SalesDocumentConfirmation,
  ): Promise<CoversVolume> {
    const response = await api.post<CoversVolume>(
      '/confirm-sales-document/',
      confirmationData,
    );
    return response.data;
  }

  /**
   * Get invoice by ID
   */
  async getInvoice(invoiceId: string | number): Promise<Invoice> {
    this.logger.debug('getInvoice request', undefined, { invoiceId });
    try {
      const response = await api.get<Invoice>(`/invoices/${invoiceId}`);
      this.logger.info('getInvoice success', undefined, {
        invoiceId,
        status: response.status,
        hasData: Boolean(response.data),
      });
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('getInvoice failed', err, undefined, { invoiceId });
      throw error;
    }
  }

  async downloadInvoiceFile(
    invoiceId: string,
  ): Promise<InvoiceFileDownloadResponse> {
    const response = await api.post<InvoiceFileDownloadResponse>(
      `/invoices/${encodeURIComponent(invoiceId)}/download`,
    );
    return response.data;
  }

  /**
   * Update invoice data
   */
  async updateInvoice(
    invoiceId: string | number,
    data: InvoiceEditForm,
  ): Promise<Invoice> {
    const response = await api.put<Invoice>(`/invoices/${invoiceId}`, data);
    return response.data;
  }

  /**
   * Delete invoice (legacy method - kept for backward compatibility)
   */
  async deleteInvoice(invoiceId: string | number): Promise<AxiosResponse> {
    return api.delete(`/invoices/${invoiceId}`);
  }

  /**
   * Delete transaction - unified method for all transaction types
   */
  async deleteTransaction(
    transactionId: string | number,
    transactionType: 'ocr' | 'manual' | 'sales_ocr' | 'sales_manual',
  ): Promise<{
    success: boolean;
    message: string;
    deleted_transaction_id: number;
    transaction_type: string;
  }> {
    this.logger.debug('deleteTransaction request', undefined, {
      transactionId,
      transactionType,
    });
    try {
      const response = await api.delete(
        `/transactions/${transactionId}?transaction_type=${transactionType}`,
      );
      this.logger.info('deleteTransaction success');
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('deleteTransaction failed', err, undefined, {
        transactionId,
        transactionType,
      });
      throw error;
    }
  }

  /**
   * Get invoices with optional filtering and pagination
   */
  async getInvoices(params?: {
    page?: number;
    per_page?: number;
    service_type?: string;
    date_from?: string;
    date_to?: string;
    search?: string;
  }): Promise<PaginatedResponse<Invoice>> {
    const response = await api.get<PaginatedResponse<Invoice>>('/invoices', {
      params,
    });
    return response.data;
  }

  /**
   * Get unified transactions (OCR, manual, sales) with optional filtering and pagination
   */
  async getUnifiedTransactions(
    params?: UnifiedTransactionFilters,
  ): Promise<PaginatedUnifiedTransactions> {
    this.logger.debug('getUnifiedTransactions request', undefined, { params });
    try {
      const response = await api.get<PaginatedUnifiedTransactions>(
        '/transactions/unified',
        { params },
      );
      this.logger.info('getUnifiedTransactions success', undefined, {
        total: response.data.total,
        items: response.data.items.length,
      });
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('getUnifiedTransactions failed', err);
      throw error;
    }
  }

  /**
   * Infer transaction type from ID alone
   */
  async getTransactionType(transactionId: string | number): Promise<{
    id: number;
    transaction_type: 'ocr' | 'manual' | 'sales_ocr' | 'sales_manual';
    table: string;
  }> {
    this.logger.debug('getTransactionType request', undefined, {
      transactionId,
    });
    try {
      const response = await api.get<{
        id: number;
        transaction_type: 'ocr' | 'manual' | 'sales_ocr' | 'sales_manual';
        table: string;
      }>(`/transactions/${transactionId}/type`);
      this.logger.info('getTransactionType success', undefined, {
        transactionId,
        type: response.data.transaction_type,
      });
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('getTransactionType failed', err, undefined, {
        transactionId,
      });
      throw error;
    }
  }

  /**
   * Get unified transaction details by ID and type
   */
  async getTransactionDetails(
    transactionId: string | number,
    transactionType: 'ocr' | 'manual' | 'sales_ocr' | 'sales_manual',
  ): Promise<Invoice & { transaction_type: string }> {
    this.logger.debug('getTransactionDetails request', undefined, {
      transactionId,
      transactionType,
    });

    // Normalize transaction type for backend API call
    const normalizedType =
      transactionType === 'sales_ocr' || transactionType === 'sales_manual'
        ? 'sales'
        : transactionType;

    try {
      const response = await api.get<Invoice & { transaction_type: string }>(
        `/transactions/${transactionId}/details?transaction_type=${normalizedType}`,
      );
      this.logger.info('getTransactionDetails success', undefined, {
        transactionId,
        transactionType,
        normalizedType,
      });
      return response.data;
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('getTransactionDetails failed', err, undefined, {
        transactionId,
        transactionType,
        normalizedType,
      });
      throw error;
    }
  }

  /**
   * Get calendarized consumption data
   */
  async getCalendarizedConsumption(params: {
    start_month: string; // Format: YYYY-MM
    end_month: string; // Format: YYYY-MM
  }): Promise<CalendarizedConsumptionResponse> {
    try {
      this.logger.debug('getCalendarizedConsumption request', undefined, {
        params,
      });

      const response = await api.get('/reports/calendarized_consumption/', {
        params,
      });

      this.logger.info('getCalendarizedConsumption success');
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('getCalendarizedConsumption failed', err, undefined, {
        params,
      });
      throw error;
    }
  }

  /**
   * Get calendarized CO2e data
   */
  async getCalendarizedCO2e(params: {
    start_month: string;
    end_month: string;
  }): Promise<CalendarizedCO2eResponse> {
    const response = await api.get<CalendarizedCO2eResponse>(
      '/emissions/calendarized/',
      {
        params,
      },
    );
    return response.data;
  }

  /**
   * Get calendarized emissions by scope data
   */
  async getCalendarizedEmissionsByScope(params: {
    start_month: string;
    end_month: string;
  }): Promise<CalendarizedEmissionsByScopeResponse> {
    const response = await api.get<CalendarizedEmissionsByScopeResponse>(
      '/emissions/calendarized-by-scope/',
      {
        params,
      },
    );
    return response.data;
  }

  /**
   * Update unified transaction details by ID and type
   */
  async updateTransactionDetails(
    transactionId: string | number,
    transactionType: 'ocr' | 'manual',
    data: InvoiceEditForm,
  ): Promise<Invoice & { transaction_type: string }> {
    this.logger.debug('updateTransactionDetails request', undefined, {
      transactionId,
      transactionType,
    });

    // For update operations, no normalization needed as sales transactions can't be updated
    // Only 'ocr' and 'manual' types can be updated, which map directly to backend types
    try {
      const response = await api.put<Invoice & { transaction_type: string }>(
        `/transactions/${transactionId}/details?transaction_type=${transactionType}`,
        data,
      );
      this.logger.info('updateTransactionDetails success', undefined, {
        transactionId,
        transactionType,
        status: response.status,
      });
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('updateTransactionDetails failed', err, undefined, {
        transactionId,
        transactionType,
      });
      throw error;
    }
  }

  // --------------------------------------------------------------------------
  // Manual Entry Endpoints
  // --------------------------------------------------------------------------

  /**
   * Log manual consumption entry
   */
  async logManualEntry(
    data: ManualConsumptionEntryPayload,
  ): Promise<ConsumptionEntry[]> {
    const response = await api.post<ConsumptionEntry[]>(
      '/log-manual-entry/',
      data,
    );
    return response.data;
  }

  /**
   * Log manual waste entry captured in bags
   */
  async logBagWasteEntry(
    data: BagWasteEntryPayload,
  ): Promise<BagWasteResponse> {
    const response = await api.post<BagWasteResponse>('/waste/bags', data);
    return response.data;
  }

  /**
   * Get consumption entries with optional filtering
   */
  async getConsumptionEntries(params?: {
    service_type?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    per_page?: number;
  }): Promise<PaginatedResponse<ConsumptionEntry>> {
    const response = await api.get<PaginatedResponse<ConsumptionEntry>>(
      '/consumption-entries',
      { params },
    );
    return response.data;
  }

  // --------------------------------------------------------------------------
  // Client Management Endpoints
  // --------------------------------------------------------------------------

  /**
   * Log client/cubierto data entry
   */
  async logClientEntry(data: ClientesForm): Promise<ClientEntry> {
    const response = await api.post<ClientEntry>('/log-clientes/', data);
    return response.data;
  }

  /**
   * Get client entries with optional filtering
   */
  async getClientEntries(params?: {
    date_from?: string;
    date_to?: string;
    periodo?: string;
    page?: number;
    per_page?: number;
  }): Promise<PaginatedResponse<ClientEntry>> {
    const response = await api.get<PaginatedResponse<ClientEntry>>(
      '/client-entries',
      { params },
    );
    return response.data;
  }

  // --------------------------------------------------------------------------
  // Goals Management Endpoints
  // --------------------------------------------------------------------------

  /**
   * Get goals for the current user
   */
  async getGoals(): Promise<Goal[]> {
    const response = await api.get<Goal[]>('/goals');
    return response.data;
  }

  /**
   * Create a new goal
   */
  async createGoal(data: CreateGoalRequest): Promise<Goal> {
    const response = await api.post<Goal>('/goals', data);
    return response.data;
  }

  /**
   * Update a goal
   */
  async updateGoal(goalId: number, data: UpdateGoalRequest): Promise<Goal> {
    const response = await api.put<Goal>(`/goals/${goalId}`, data);
    return response.data;
  }

  /**
   * Delete a goal
   */
  async deleteGoal(goalId: number): Promise<void> {
    await api.delete(`/goals/${goalId}`);
  }

  /**
   * Recalculate baseline for a specific goal
   */
  async recalculateGoalBaseline(
    goalId: number,
    confirmed: true,
  ): Promise<{ message: string; baseline_data: Record<string, unknown> }> {
    const response = await api.post<{
      message: string;
      baseline_data: Record<string, unknown>;
    }>(`/goals/${goalId}/recalculate-baseline`, { confirmed });
    return response.data;
  }

  /**
   * Recalculate baselines for all active goals
   */
  async recalculateAllBaselines(confirmed: true): Promise<{
    message: string;
    results: Record<string, unknown>;
  }> {
    const response = await api.post<{
      message: string;
      results: Record<string, unknown>;
    }>('/goals/recalculate-baselines', { confirmed });
    return response.data;
  }

  // --------------------------------------------------------------------------
  // Analytics Endpoints
  // --------------------------------------------------------------------------

  /**
   * Get analytics summary with key indicators
   */
  async getAnalyticsSummary(): Promise<AnalyticsSummary> {
    const response = await api.get<AnalyticsSummary>('/analytics/summary');
    return response.data;
  }

  /**
   * Get paginated transactions with optional filtering
   */
  async getAnalyticsTransactions(
    filters?: AnalyticsFilters,
  ): Promise<PaginatedTransactions> {
    const params = new URLSearchParams();

    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.service_type) {
      params.append('service_type', filters.service_type);
    }
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await api.get<PaginatedTransactions>(
      `/analytics/transactions?${params.toString()}`,
    );
    return response.data;
  }

  /**
   * Get analytics chart data with per-dish consumption metrics
   */
  async getAnalyticsCharts(
    filters?: ChartFilters,
  ): Promise<AnalyticsChartResponse> {
    const params = new URLSearchParams();

    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.period_type) params.append('period_type', filters.period_type);

    const response = await api.get<AnalyticsChartResponse>(
      `/analytics/charts?${params.toString()}`,
    );
    return response.data;
  }

  /**
   * Get total consumption chart data directly from consumption_entries table
   */
  async getTotalConsumptionChart(
    filters?: ChartFilters,
  ): Promise<TotalConsumptionChartResponse> {
    const params = new URLSearchParams();

    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.period_type) params.append('period_type', filters.period_type);

    const response = await api.get<TotalConsumptionChartResponse>(
      `/analytics/total-consumption-chart?${params.toString()}`,
    );

    return response.data;
  }

  /**
   * Get progress score series within a month range
   */
  async getProgressScoreSeries(params: {
    start_month: string;
    end_month: string;
  }): Promise<ProgressScoreSeriesResponse> {
    const response = await api.get<ProgressScoreSeriesResponse>(
      '/analytics/progress-score',
      { params },
    );
    return response.data;
  }

  /**
   * Get current progress score summary
   */
  async getProgressScoreSummary(): Promise<ProgressScoreSummaryResponse> {
    const response = await api.get<ProgressScoreSummaryResponse>(
      '/analytics/progress-score/summary',
    );
    return response.data;
  }

  /**
   * Get transaction summary statistics
   */
  async getTransactionSummary(
    filters?: Omit<AnalyticsFilters, 'page' | 'limit'>,
  ): Promise<TransactionSummary> {
    const params = new URLSearchParams();

    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.service_type) {
      params.append('service_type', filters.service_type);
    }

    const response = await api.get<TransactionSummary>(
      `/analytics/transactions/summary?${params.toString()}`,
    );
    return response.data;
  }

  // --------------------------------------------------------------------------
  // File Upload Utilities
  // --------------------------------------------------------------------------

  /**
   * Create FormData for file upload with additional fields
   */
  createFileFormData(
    file: File,
    additionalFields?: Record<string, string | number>,
  ): FormData {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalFields) {
      Object.entries(additionalFields).forEach(([key, value]) => {
        formData.append(key, String(value));
      });
    }

    return formData;
  }

  /**
   * Upload file with progress tracking
   */
  async uploadFileWithProgress({
    endpoint = '/upload/with-progress',
    file,
    additionalFields,
    onProgress,
    headers,
  }: {
    endpoint?: string;
    file: File;
    additionalFields?: Record<string, string | number>;
    onProgress?: (progress: number) => void;
    headers?: Record<string, string>;
  }): Promise<AxiosResponse> {
    this.logger.debug('uploadFileWithProgress request', undefined, {
      endpoint,
      fileName: file.name,
      fileSize: file.size,
      hasAdditionalFields: Boolean(
        additionalFields && Object.keys(additionalFields).length,
      ),
    });

    try {
      const formData = this.createFileFormData(file, additionalFields);

      const response = await api.post(endpoint, formData, {
        ...(headers && { headers }),
        onUploadProgress: (progressEvent) => {
          if (!onProgress) return;
          const { loaded, total } = progressEvent;
          if (!total) {
            onProgress(0);
            return;
          }
          const percentage = Math.min(Math.round((loaded / total) * 100), 100);
          onProgress(percentage);
        },
      });
      this.logger.info('uploadFileWithProgress success', undefined, {
        endpoint,
        fileName: file.name,
        status: response.status,
      });
      return response;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('uploadFileWithProgress failed', err, undefined, {
        endpoint,
        fileName: file.name,
      });
      throw error;
    }
  }

  // --------------------------------------------------------------------------
  // Covers Volume Management Endpoints (Sales Data)
  // --------------------------------------------------------------------------

  /**
   * Get covers volumes with optional filtering and pagination
   */
  async getCoversVolumes(
    params: {
      start_date?: string;
      end_date?: string;
    } = {},
  ): Promise<PaginatedResponse<CoversVolume>> {
    this.logger.debug('getCoversVolumes request', undefined, { params });
    try {
      const response = await api.get<PaginatedResponse<CoversVolume>>(
        '/covers-volumes',
        { params },
      );
      this.logger.info('getCoversVolumes success');
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('getCoversVolumes failed', err, undefined, { params });
      throw error;
    }
  }

  /**
   * Create a new covers volume entry
   */
  async createCoversVolume(data: CoversVolumeCreate): Promise<CoversVolume> {
    this.logger.debug('createCoversVolume request', undefined, { data });
    try {
      const response = await api.post<CoversVolume>('/covers-volumes', data);
      this.logger.info('createCoversVolume success', undefined, {
        id: response.data.id,
      });
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('createCoversVolume failed', err, undefined, { data });
      throw error;
    }
  }

  /**
   * Get a specific covers volume by ID
   */
  async getCoversVolume(volumeId: number): Promise<CoversVolume> {
    this.logger.debug('getCoversVolume request', undefined, { volumeId });
    try {
      const response = await api.get<CoversVolume>(
        `/covers-volumes/${volumeId}`,
      );
      this.logger.info('getCoversVolume success', undefined, { volumeId });
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('getCoversVolume failed', err, undefined, { volumeId });
      throw error;
    }
  }

  /**
   * Update a covers volume entry
   */
  async updateCoversVolume(
    volumeId: number,
    data: CoversVolumeUpdate,
  ): Promise<CoversVolume> {
    this.logger.debug('updateCoversVolume request', undefined, {
      volumeId,
      data,
    });
    try {
      const response = await api.put<CoversVolume>(
        `/covers-volumes/${volumeId}`,
        data,
      );
      this.logger.info('updateCoversVolume success', undefined, { volumeId });
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('updateCoversVolume failed', err, undefined, {
        volumeId,
      });
      throw error;
    }
  }

  /**
   * Delete a covers volume entry
   */
  async deleteCoversVolume(volumeId: number): Promise<void> {
    this.logger.debug('deleteCoversVolume request', undefined, { volumeId });
    try {
      await api.delete(`/covers-volumes/${volumeId}`);
      this.logger.info('deleteCoversVolume success', undefined, { volumeId });
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('deleteCoversVolume failed', err, undefined, {
        volumeId,
      });
      throw error;
    }
  }

  /**
   * Get covers volume statistics and summary
   */
  async getCoversVolumeStats(params: {
    start_date?: string;
    end_date?: string;
  }): Promise<CoversVolumeStats> {
    this.logger.debug('getCoversVolumeStats request', undefined, { params });
    const response = await api.get<CoversVolumeStats>('/covers-volumes/stats', {
      params,
    });
    return response.data;
  }

  // --------------------------------------------------------------------------
  // Utility Methods
  // --------------------------------------------------------------------------

  /**
   * Check if user is authenticated by testing /me endpoint
   */
  async checkAuth(): Promise<boolean> {
    try {
      await this.me();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Initialize API service - ensures CSRF token is available
   */
  async initialize(): Promise<void> {
    try {
      await this.csrf();
    } catch (error) {
      this.logger.warn('Failed to initialize CSRF token', undefined, {
        error,
      });
    }
  }

  /**
   * Get axios instance for custom requests
   */
  getAxiosInstance() {
    return api;
  }

  /**
   * Create a custom request with full control
   */
  async request<T = unknown>(
    config: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return api.request<T>(config);
  }

  // ==========================================================================
  // Smart Sorting
  // ==========================================================================

  async initSmartSortingUpload(
    data: SmartSortingUploadInitRequest,
  ): Promise<SmartSortingUploadInitResponse> {
    const response = await api.post<SmartSortingUploadInitResponse>(
      '/smart-sorting/files/init',
      data,
    );
    return response.data;
  }

  async finalizeSmartSortingFile(
    data: SmartSortingFinalizeRequest,
  ): Promise<SmartSortingFinalizeResponse> {
    const response = await api.post<SmartSortingFinalizeResponse>(
      '/smart-sorting/files',
      data,
    );
    return response.data;
  }

  async getSmartSortingSummary(): Promise<SmartSortingSummaryResponse> {
    const response = await api.get<SmartSortingSummaryResponse>(
      '/smart-sorting/summary',
    );
    return response.data;
  }

  async getSmartSortingFiles(
    params: SmartSortingFilesListParams = {},
  ): Promise<SmartSortingFilesListResponse> {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.set('category', params.category);
    if (params.status) queryParams.set('status', params.status);
    if (params.search) queryParams.set('search', params.search);
    if (params.page) queryParams.set('page', String(params.page));
    if (params.per_page) queryParams.set('per_page', String(params.per_page));

    const qs = queryParams.toString();
    const path = qs ? `/smart-sorting/files?${qs}` : '/smart-sorting/files';
    const response = await api.get<SmartSortingFilesListResponse>(path);
    return response.data;
  }

  async downloadSmartSortingFile(
    fileId: string,
  ): Promise<SmartSortingFileDownloadResponse> {
    const response = await api.post<SmartSortingFileDownloadResponse>(
      `/smart-sorting/files/${encodeURIComponent(fileId)}/download`,
    );
    return response.data;
  }

  async getUserFiles(
    params: UserFilesListParams,
  ): Promise<UserFilesListResponse> {
    const queryParams = new URLSearchParams({
      page: String(params.page),
      per_page: String(params.per_page),
    });
    if (params.kind) queryParams.set('kind', params.kind);
    if (params.search) queryParams.set('search', params.search);

    const response = await api.get<UserFilesListResponse>(
      `/user-files?${queryParams.toString()}`,
    );
    return response.data;
  }

  // ==========================================================================
  // QR sharing
  // ==========================================================================

  async previewQrShareScope(
    scope: DomainShareScope,
  ): Promise<QrShareScopePreviewResponse> {
    const response = await api.get<QrShareScopePreviewResponse>(
      `/qr-shares/scope-preview?scope=${encodeURIComponent(scope)}`,
    );
    return response.data;
  }

  async createQrShare(payload: QrShareCreateRequest): Promise<QrShareDetail> {
    const response = await api.post<QrShareDetail>('/qr-shares', payload);
    return response.data;
  }

  async getQrShares(
    params: QrShareListParams = {},
  ): Promise<QrShareListResponse> {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.set('status', params.status);
    if (params.share_scope) queryParams.set('share_scope', params.share_scope);
    const query = queryParams.toString();

    const response = await api.get<QrShareListResponse>(
      query ? `/qr-shares?${query}` : '/qr-shares',
    );
    return response.data;
  }

  async getQrShare(shareId: string): Promise<QrShareDetail> {
    const response = await api.get<QrShareDetail>(
      `/qr-shares/${encodeURIComponent(shareId)}`,
    );
    return response.data;
  }

  async updateQrShare(
    shareId: string,
    payload: QrShareUpdateRequest,
  ): Promise<QrShareDetail> {
    const response = await api.patch<QrShareDetail>(
      `/qr-shares/${encodeURIComponent(shareId)}`,
      payload,
    );
    return response.data;
  }

  async archiveQrShare(shareId: string): Promise<QrShareDetail> {
    const response = await api.post<QrShareDetail>(
      `/qr-shares/${encodeURIComponent(shareId)}/archive`,
    );
    return response.data;
  }

  async revokeQrShare(shareId: string): Promise<QrShareDetail> {
    const response = await api.post<QrShareDetail>(
      `/qr-shares/${encodeURIComponent(shareId)}/revoke`,
    );
    return response.data;
  }

  async rotateQrShareToken(shareId: string): Promise<QrShareDetail> {
    const response = await api.post<QrShareDetail>(
      `/qr-shares/${encodeURIComponent(shareId)}/rotate-token`,
    );
    return response.data;
  }
}

// ============================================================================
// Export Singleton Instance
// ============================================================================

export const apiService = new ApiService();

export function getCurrentCsrfToken(): string | null {
  return cachedCsrfToken;
}

export function setCsrfToken(token: string | null): void {
  cachedCsrfToken = token;
}

async function ensureCsrfToken(forceRefresh = false): Promise<string> {
  if (!forceRefresh && cachedCsrfToken) {
    return cachedCsrfToken;
  }

  if (forceRefresh) {
    cachedCsrfToken = null;
  }

  if (!csrfTokenPromise) {
    csrfTokenPromise = fetchCsrfToken().finally(() => {
      csrfTokenPromise = null;
    });
  }

  return csrfTokenPromise;
}

async function fetchCsrfToken(): Promise<string> {
  const response = await api.get<{ status: string; csrfToken?: string }>(
    '/csrf',
    {
      headers: {
        'Cache-Control': 'no-cache',
      },
    },
  );

  const token = response.data.csrfToken ?? '';
  cachedCsrfToken = token;
  return token;
}

// ============================================================================
// Export Types for External Use
// ============================================================================

export type { ApiError, AxiosError };

// ============================================================================
// Default Export
// ============================================================================

export default apiService;
